import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";
import { Select, Spin, Tooltip } from "../utils/local-antd";
import type { SelectProps } from "../utils/local-antd";
import debounce from "lodash/debounce";
import React, { useMemo, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";

import { Spacing } from "../theme/Spacing";
import { AtiraIcon } from "./AtiraIcon";
import { Flex } from "./Flex";
import { Text } from "./Text";
import { useTranslation } from "react-i18next";

const StyledSelectContainer = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
  width: 100%;

  .ant-select {
    height: 2.3rem;
  }

  .ant-select-selector:hover {
    border: 1px solid ${({ theme }) => theme.main} !important;
  }
`;

const InfoSpan = styled.span`
  padding-inline-end: calc(${Spacing.s} / 2);
`;

export interface DropDownRemoteSearchProps<ValueType = any>
  extends Omit<SelectProps<ValueType | ValueType[]>, "options" | "children"> {
  fetchOptions: (search: string) => Promise<ValueType[]>;
  debounceTimeout?: number;
  title?: string;
  defaultOptions?: ValueType[];
  info?: string;
  errorMessage?: string;
  valid?: boolean;
  required?: boolean;
}

export function DropDownRemoteSearch<
  ValueType extends {
    key?: string;
    label: React.ReactNode;
    value: string | number;
  } = any,
>({
  fetchOptions,
  debounceTimeout = 800,
  title,
  defaultOptions = [],
  info,
  errorMessage,
  valid = true,
  ...props
}: DropDownRemoteSearchProps<ValueType>) {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>(() => defaultOptions);
  const fetchRef = useRef(0);
  const theme = useTheme();
  const { t } = useTranslation();

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          // for fetch callback order
          return;
        }

        setOptions(newOptions);
        setFetching(false);
      });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  return (
    <StyledSelectContainer>
      {title ? (
        <Text>
          {info ? (
            <Tooltip title={info}>
              <InfoSpan>
                <AtiraIcon icon={faInfoCircle} color="main" size="sm" />
              </InfoSpan>
            </Tooltip>
          ) : null}
          {title}
          {props.required ? (
            <span style={{ color: theme.danger }}> *</span>
          ) : (
            ""
          )}
        </Text>
      ) : null}
      <Select
        labelInValue
        allowClear
        showSearch={{ filterOption: false, onSearch: debounceFetcher }}
        placeholder={t("common.search.min3")}
        notFoundContent={fetching ? <Spin size="small" /> : null}
        {...props}
        options={options}
      />

      {valid === false && errorMessage ? (
        <Text color="danger" fontSize="s">
          {errorMessage}
        </Text>
      ) : null}
    </StyledSelectContainer>
  );
}
